import React, { FC } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { getActionPriceListsTemplate } from 'apollo/templates/action';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { ActionPriceList, PriceListAction } from 'models/action';

type InternalCodeAutocompleteProps = {
    value: string;
    onChange: (internalCode: string) => void;
};

// Free-text autocomplete for the internal code: suggests actions from the lab's
// default action price list (matched by code) but accepts any typed value.
const InternalCodeAutocomplete: FC<InternalCodeAutocompleteProps> = ({ value, onChange }) => {
    const { t: translate } = useTranslation(['actions-materials']);
    const { showBoundary } = useDenthubErrorBoundary();

    const { data: { actionPriceLists } = {} } = useQuery(getActionPriceListsTemplate, {
        onError(error) {
            showBoundary(error);
        },
    });

    const defaultPriceListActions: PriceListAction[] =
        (actionPriceLists || []).find((priceList: ActionPriceList) => priceList.isStandardPriceList)?.actions || [];

    return (
        <Autocomplete
            id="internal-code"
            freeSolo
            options={defaultPriceListActions}
            inputValue={value}
            onInputChange={(_, newInputValue) => onChange(newInputValue)}
            getOptionLabel={(option: PriceListAction | string) =>
                typeof option === 'string' ? option : option.actionCode || ''
            }
            filterOptions={(options, state) =>
                options.filter((option) =>
                    (option.actionCode || '').toLowerCase().includes(state.inputValue.toLowerCase()),
                )
            }
            renderOption={(props, option) => (
                <li {...props} key={option._id}>
                    {option.actionCode} - {option.description}
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    margin="dense"
                    label={translate('actions-materials:INTERNAL_CODE')}
                    helperText={translate('actions-materials:INTERNAL_CODE_INFO')}
                    FormHelperTextProps={{ sx: { color: 'info.main' } }}
                />
            )}
        />
    );
};

export default InternalCodeAutocomplete;
